import Game from '../../../../Game';
import Renderable from '../../../../Component/Renderable';
import GameObject, { GameObjectEvent } from '../../../../GameObject/GameObject';
import LeaderboardHelper from '../../../../System/UI/LeaderboardHelper';
import { applyDefaultButtonAnimation } from '../../UIHelper';
import { GameActions } from '../../../../State/Game/GameActions';
import { IFriendData } from '../../../../Player/PlayerProperty/Social/IPlayerSocialData';
import DateHelper from '../../../../Utility/DateHelper';
import { FRIEND_GIFT_TIMER_COOLDOWN } from '../../../../Constants/Timers';
import Interactable from '../../../../Component/Interactable';
import { ContextType } from '../../../../State/Game/ContextType';
import { IGameState } from '../../../../State/Game/IGameState';
import { IScoreEntry } from '../../../../Utility/SDK/Drivers/IScoreEntry';
import Element from '../../../../Prototype/UI/Element';
import { LevelPopupFriendLayout } from '../../../../Layouts/LevelPopup/LevelPopupFriendLayout';
import TextComponent from '../../../../Component/Render/Text';
import SpriteComponent from '../../../../Component/Render/Sprite';
import { fit } from '../../../../Utility/BoundsHelper';
import { Layout } from '../../../../Utility/layout/createLayout';

const PLACEMENT_COLORS: string[] = ['#71120a', '#162c42', '#4d140e', '#8d3317'];

type SideFriendItemProps = {
    playerData: IScoreEntry;
    index: number;
}

export class SideFriendItem extends Element {
    private playerData: IScoreEntry;
    private layout: any;

    public create(props: SideFriendItemProps): void {
        const height = 103;
        const y = props.index * height;

        super.create({
            type: 'group',
            layout: {
                anchors: [0, 0, 0, 0],
                pivot: [0, 0],
                offsets: [0, -y, 0, -y],
            },
        });

        this.playerData = props.playerData;
        this.layout = new LevelPopupFriendLayout();
        this.addChild(this.layout.root);

        applyDefaultButtonAnimation(this.layout.coin);
        this.layout.coin.on(GameObjectEvent.SELECTED, this.onSend.bind(this));

        this.init();
    }

    public init(): void {
        const data = this.playerData;

        // If current player adjust to display current user plate
        if (data.user.isCurrentUser) {
            this.layout.timeText.getComponent(TextComponent).text = '';
            this.layout.coin.getComponent(Renderable).visible = false;
            this.setButtonInput(this.layout.coin, false);
        } else {
            this.setButtonInput(this.layout.coin, true);
        }

        const placement: number = Math.min(data.rank, 4);
        this.layout.bg.getComponent(SpriteComponent).texture = `friends_cover_${placement}`;

        // Get avatar texture and then set it
        LeaderboardHelper.getPlayerAvatar(data).then((texture: string) => {
            let spriteComponent;
            if (
                this.layout.photo &&
                !this.layout.photo.destroyed && (
                    spriteComponent = this.layout.photo.getComponent(SpriteComponent)
                )
            ) {
                spriteComponent.texture = texture;
                fit(this.layout.photo, 68, 68);
            }
        });

        // Set player name and star count
        const playerNameText = this.layout.nameText.getComponent(TextComponent);
        playerNameText.text = data.user.handle;
        playerNameText.style = {
            ...Game.style.getStyle(`popup.friends_hub.placement.top_name`),
            fill: PLACEMENT_COLORS[placement - 1],
            stroke: PLACEMENT_COLORS[placement - 1],
        };

        const starCountText = this.layout.scoreText.getComponent(TextComponent);
        starCountText.text = data.score.toString();
        starCountText.style = {
            ...Game.style.getStyle(`popup.friends_hub.placement.top_score`),
            stroke: PLACEMENT_COLORS[placement - 1],
            fontSize: 26,
        };

        this.updateTimers();
    }

    public updateTimers() {
        if (this.playerData.user.isCurrentUser || this.destroyed) return;

        const state: IGameState = Game.store.getState();
        const friendsData: IFriendData[] = state.playerState.social.friendData;

        const coin: GameObject = this.layout.coin;
        const timerText = this.layout.timeText.getComponent(TextComponent);
        const coinRenderable = coin.getComponent(SpriteComponent);

        const currentFriend: IFriendData = friendsData
            .find((friend: IFriendData) => friend.playerId === this.playerData.user.playerId);

        if (currentFriend && currentFriend.giftCooldown !== null) {
            const cooldown: Date = DateHelper.getDate(currentFriend.giftCooldown);
            const endTime: Date = DateHelper.getDate(cooldown.getTime() + FRIEND_GIFT_TIMER_COOLDOWN);
            coinRenderable.texture = 'friends_coin2';

            if (endTime.getTime() - DateHelper.now.getTime() < 0) {
                // Hide timer
                coinRenderable.texture = 'friends_coin';
                timerText.text = '';

                this.setButtonInput(coin, true);
            } else {
                timerText.text = DateHelper.timeTo(
                    DateHelper.now,
                    DateHelper.getDate(
                        cooldown.getTime() + FRIEND_GIFT_TIMER_COOLDOWN,
                    ),
                    'hh:mm:ss',
                );

                this.setButtonInput(coin, false);
            }
        } else {
            // Hide timer
            coinRenderable.texture = 'friends_coin';
            timerText.text = '';

            this.setButtonInput(coin, true);
        }
    }

    private onSend() {
        if (!this.playerData || this.playerData.user.isCurrentUser) return;

        GameActions.setContext(ContextType.SEND_FRIEND_GIFT, {
            friendId: this.playerData.user.playerId,
            placement: 'TopList',
        });
    }

    public destroy() {
        super.destroy();
    }

    private setButtonInput(button: GameObject, enabled: boolean = true) {
        button.addComponent(Interactable);
        const interactable: Interactable = button.getComponent(Interactable);
        interactable.enabled = enabled;
        interactable.buttonMode = enabled;
    }
}
